/**
 * Матрица доступности полей виджета для аудио-операций Suno.
 *
 * Не все параметры `SunoAdapter.generate()` применимы ко всем операциям.
 * Например:
 *   - `personaId` не поддерживается для cover / add_vocals / add_instrumental
 *   - `instrumental: true` не имеет смысла для «+ Вокал» (наоборот — его
 *     надо принудительно выключить)
 *   - `vocal_gender` / `custom_mode` / `lyrics` не применимы для
 *     «+ Инструментал» (там нет вокала вообще)
 *
 * UI дизейблит недоступные pills и показывает tooltip-объяснение.
 * Если юзер всё-таки отправит значение — Suno проигнорирует или отклонит.
 */
import type { AudioOperation } from '../../../context/GenerateContext'

export interface FieldStatus {
	enabled: boolean
	/** Человеческое объяснение почему недоступно. Показывается в tooltip. */
	reason?: string
	/** Для toggle-полей: значение которое принудительно ставится
	 *  пока operation активна (например instrumental=true для add_instrumental). */
	forcedValue?: boolean
}

export interface AudioFieldAvailability {
	persona: FieldStatus
	instrumental: FieldStatus
	vocalGender: FieldStatus
	customMode: FieldStatus
	lyrics: FieldStatus
	style: FieldStatus
	model: FieldStatus
	title: FieldStatus
	prompt: FieldStatus
}

const ENABLED: FieldStatus = { enabled: true }

export function getAudioFieldAvailability(
	op?: AudioOperation,
): AudioFieldAvailability {
	const all: AudioFieldAvailability = {
		persona: ENABLED,
		instrumental: ENABLED,
		vocalGender: ENABLED,
		customMode: ENABLED,
		lyrics: ENABLED,
		style: ENABLED,
		model: ENABLED,
		title: ENABLED,
		prompt: ENABLED,
	}

	switch (op) {
		case 'cover':
			return {
				...all,
				persona: {
					enabled: false,
					reason: 'Suno не поддерживает выбор Персоны для Cover',
				},
			}

		case 'add_vocals':
			return {
				...all,
				persona: {
					enabled: false,
					reason: 'Suno не поддерживает выбор Персоны для операции «+ Вокал»',
				},
				instrumental: {
					enabled: false,
					reason: 'Для «+ Вокал» инструментал — это основа трека. Голос добавляется поверх него.',
					forcedValue: false,
				},
			}

		case 'add_instrumental':
			return {
				...all,
				persona: {
					enabled: false,
					reason: 'Suno не поддерживает выбор Персоны для «+ Инструментал»',
				},
				instrumental: {
					enabled: false,
					reason: 'Операция сама добавляет инструментал — переключатель недоступен',
					forcedValue: true,
				},
				vocalGender: {
					enabled: false,
					reason: 'Голос не генерируется — добавляется только инструментал',
				},
				customMode: {
					enabled: false,
					reason: 'Текст не генерируется для «+ Инструментал»',
				},
				lyrics: {
					enabled: false,
					reason: 'Текст песни не используется — укажи только жанр/описание инструментала ниже',
				},
			}

		// extend / generate / (sounds рулится submode отдельно) — всё доступно
		case 'extend':
		case 'generate':
		default:
			return all
	}
}
